import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { sessionsApi } from "../../api/sessionsApi";
import { appointmentsApi } from "../../api/appointmentsApi";
import { useAuth } from "../../auth/authStore";
import { useToast } from "../../ui/toast/ToastProvider";
import { resolveUrl } from "../../utils/urls";
import { assignExternalUrl } from "../../utils/safeUrl";

const PAID_STATUSES = new Set(["PAID", "CONFIRMED", "SUCCEEDED"]);
const PLACEHOLDER_GRADIENTS = ["1", "2", "3", "4", "5", "6"];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toCode(value) {
  return String(value || "").trim().toUpperCase();
}

function pickStart(row) {
  return row?.startDateTime || row?.start_datetime || null;
}

function pickEnd(row) {
  return row?.endDateTime || row?.end_datetime || null;
}

function pickCover(row) {
  return row?.coverImg || row?.cover_img || row?.image || row?.coverUrl || null;
}

function pickCapacity(row) {
  return row?.capacityClients ?? row?.capacity_clients ?? row?.capacity ?? null;
}

function pickParticipants(row) {
  return row?.participantsCount ?? row?.clientsCount ?? row?.membersCount ?? (Array.isArray(row?.participants) ? row.participants.length : 0);
}

function pickPrice(row) {
  return row?.priceAtTime ?? row?.price_at_time ?? row?.price ?? null;
}

function isConferenceShadowSession(row) {
  const source = row && typeof row === "object" ? row : {};
  const typeToken = String(
    source?.kind ??
    source?.sessionKind ??
    source?.session_kind ??
    source?.bookingType ??
    source?.booking_type ??
    source?.type ??
    ""
  ).toUpperCase();
  if (typeToken.includes("CONFERENCE") || typeToken.includes("DIRECT")) return true;

  const capacity = Number(source?.capacityClients ?? source?.capacity_clients ?? source?.capacity ?? NaN);
  const minCapacity = Number(source?.minCapacity ?? source?.min_capacity ?? NaN);
  const price = Number(source?.priceAtTime ?? source?.price_at_time ?? source?.price ?? NaN);
  return (
    Number.isFinite(capacity) &&
    capacity === 1 &&
    Number.isFinite(minCapacity) &&
    minCapacity === 1 &&
    (!Number.isFinite(price) || price <= 0)
  );
}

function isJoinAllowed(row) {
  const source = row && typeof row === "object" ? row : {};
  const raw = source?.allowJoin ?? source?.allow_join;
  if (raw == null) return true;
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") return raw === 1;
  const token = String(raw).trim().toLowerCase();
  if (!token) return true;
  return token === "true" || token === "1" || token === "yes";
}

function pickFormat(row) {
  const raw = String(row?.format || row?.sessionFormat || row?.type || "").toUpperCase();
  if (raw.includes("OFFLINE") || raw.includes("ОФЛ") || row?.offline) return "Офлайн";
  if (row?.location && !row?.telemostUrl && !row?.telemost_url) return "Офлайн";
  return "Онлайн";
}

function pickThemes(row) {
  return row?.themeLabels?.length ? row.themeLabels : row?.themes || [];
}

function pickMethods(row) {
  return row?.methodLabels?.length ? row.methodLabels : row?.methods || [];
}

function joinTitles(arr, limit = 99) {
  return (arr || [])
    .map((x) => {
      if (!x) return null;
      if (typeof x === "string") return x;
      return x?.title || x?.name || x?.label || null;
    })
    .filter(Boolean)
    .slice(0, limit);
}

function formatPrice(value) {
  if (value == null || value === "") return "Бесплатно";
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "Бесплатно";
  return `${n.toLocaleString("ru-RU")} ₽`;
}

function fmtDateTimeRange(startIso, endIso) {
  try {
    const s = startIso ? new Date(startIso) : null;
    const e = endIso ? new Date(endIso) : null;
    if (!s || Number.isNaN(s.getTime())) return "Дата уточняется";
    const dd = `${pad2(s.getDate())}.${pad2(s.getMonth() + 1)}.${s.getFullYear()}`;
    const st = `${pad2(s.getHours())}:${pad2(s.getMinutes())}`;
    const et = e && !Number.isNaN(e.getTime()) ? `${pad2(e.getHours())}:${pad2(e.getMinutes())}` : "";
    return et ? `${dd} • ${st}–${et}` : `${dd} • ${st}`;
  } catch {
    return "Дата уточняется";
  }
}

function formatDateShort(startIso) {
  try {
    const s = startIso ? new Date(startIso) : null;
    if (!s || Number.isNaN(s.getTime())) return "Дата уточняется";
    return s.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
  } catch {
    return "Дата уточняется";
  }
}

function formatTime(startIso) {
  try {
    const s = startIso ? new Date(startIso) : null;
    if (!s || Number.isNaN(s.getTime())) return "—";
    return `${pad2(s.getHours())}:${pad2(s.getMinutes())}`;
  } catch {
    return "—";
  }
}

function diffMinutes(startIso, endIso) {
  try {
    const s = new Date(startIso);
    const e = new Date(endIso);
    const ms = e.getTime() - s.getTime();
    if (!Number.isFinite(ms) || ms <= 0) return null;
    return Math.round(ms / 60000);
  } catch {
    return null;
  }
}

function humanStatus(status) {
  const code = toCode(status);
  if (["OPEN", "PUBLISHED", "ACTIVE"].includes(code)) return "Открыта";
  if (["PAID", "BOOKED", "CONFIRMED", "PENDING_PAYMENT"].includes(code)) return "Оформляется";
  if (["COMPLETED", "DONE"].includes(code)) return "Завершена";
  if (["CANCELLED", "CANCELED", "CLOSED"].includes(code)) return "Закрыта";
  return code || "Сессия";
}

function statusKind(status) {
  const code = toCode(status);
  if (["OPEN", "PUBLISHED", "ACTIVE"].includes(code)) return "open";
  if (["PAID", "BOOKED", "CONFIRMED", "PENDING_PAYMENT"].includes(code)) return "pending";
  if (["COMPLETED", "DONE"].includes(code)) return "completed";
  if (["CANCELLED", "CANCELED", "CLOSED"].includes(code)) return "cancelled";
  return "pending";
}

function readPaymentUrl(res) {
  return res?.confirmationUrl || res?.confirmation_url || res?.url || res?.payUrl || res?.paymentUrl || null;
}

function pickTelemostUrl(x) {
  return String(x?.telemostUrl || x?.telemost_url || x?.telemostLink || x?.meetingUrl || "").trim() || null;
}

function pickPaymentUrl(x) {
  return String(x?.paymentUrl || x?.payment_url || x?.confirmationUrl || x?.confirmation_url || x?.payUrl || "").trim() || null;
}

function isPaidStatus(status) {
  return PAID_STATUSES.has(toCode(status));
}

function isPaidAppointmentLike(row) {
  const source = row && typeof row === "object" ? row : {};
  const statusTokens = [
    source?.status,
    source?.bookingStatus,
    source?.paymentStatus,
    source?.memberPaymentStatus,
    source?.member_payment_status,
  ].map(toCode);

  if (statusTokens.some((token) => PAID_STATUSES.has(token))) return true;
  return Boolean(pickTelemostUrl(source));
}

function pickGroupSessionIdFromAppointment(a) {
  return (
    a?.groupSessionId ??
    a?.sessionId ??
    a?.session_id ??
    a?.group_session_id ??
    a?.groupSession?.id ??
    a?.session?.id ??
    null
  );
}

function participantLabel(user) {
  return user?.displayName || user?.name || user?.fullName || user?.email || "Участник";
}

function initials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "GS";
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function pickPsychologist(detail, me) {
  return detail?.psychologist || detail?.owner || detail?.author || detail?.host || (me ? {
    name: me?.name || me?.fullName || me?.login || me?.email,
    avatarUrl: me?.avatarUrl || me?.avatar_url || me?.photoUrl || null,
  } : null);
}

function pickHostRole(host) {
  return host?.specialization || host?.subtitle || host?.profession || host?.roleTitle || "Психолог";
}

function pickHostBio(detail, host) {
  return detail?.hostBio || detail?.bio || host?.bio || host?.about || detail?.description || "Ведущий помогает участникам безопасно пройти сессию, удерживает фокус группы и поддерживает бережный ритм работы.";
}

function pickExperienceYears(detail, host) {
  const raw = detail?.experienceYears ?? host?.experienceYears ?? host?.yearsExperience ?? host?.experience ?? null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n);
}

function GsMiniIcon({ name }) {
  const common = { fill: "none", stroke: "currentColor", strokeWidth: 1.9, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "calendar":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5.5" width="17" height="15" rx="3" {...common} /><path d="M7.5 3.75v3.5M16.5 3.75v3.5M3.5 10.5h17" {...common} /></svg>;
    case "wifi":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9.5a13 13 0 0 1 16 0M7 13a8.4 8.4 0 0 1 10 0M10.2 16.2a3.5 3.5 0 0 1 3.6 0" {...common} /><circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" /></svg>;
    case "users":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16.6 18.2a4.9 4.9 0 0 0-9.2 0M15.5 9.2a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z" {...common} /><path d="M18 16.6a4.4 4.4 0 0 1 2.5 1.8M17.7 7.6a2.8 2.8 0 1 1 0 5.5" {...common} /></svg>;
    case "user":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5.5 19a6.5 6.5 0 0 1 13 0" {...common} /><circle cx="12" cy="8.25" r="3.25" {...common} /></svg>;
    case "clock":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.25" {...common} /><path d="M12 7.8v4.6l3 1.8" {...common} /></svg>;
    case "shield":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.6c2 .9 4.1 1.5 6.4 1.8v5.2c0 4.1-2.4 7.2-6.4 9.8-4-2.6-6.4-5.7-6.4-9.8V5.4c2.3-.3 4.4-.9 6.4-1.8Z" {...common} /><path d="m9.2 12.1 1.8 1.8 3.9-4" {...common} /></svg>;
    case "spark":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 1.4 4.1L17.5 8.5l-4.1 1.4L12 14l-1.4-4.1L6.5 8.5l4.1-1.4L12 3Zm6.2 10.2.7 2 .7-2 2-.7-2-.7-.7-2-.7 2-2 .7 2 .7Zm-12 4 .9 2.5.9-2.5 2.5-.9-2.5-.9-.9-2.5-.9 2.5-2.5.9 2.5.9Z" fill="currentColor" stroke="none" /></svg>;
    case "chat":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 17.5h8.8l3.7 2.8v-2.8h.5a2 2 0 0 0 2-2V7.3a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8.2a2 2 0 0 0 2 2Z" {...common} /></svg>;
    case "lock":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="10.5" width="14" height="10" rx="3" {...common} /><path d="M8 10.5V8.7a4 4 0 1 1 8 0v1.8" {...common} /></svg>;
    case "check":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.2" {...common} /><path d="m8.6 12 2.3 2.4 4.7-4.8" {...common} /></svg>;
    case "card":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="6" width="17" height="12" rx="2.8" {...common} /><path d="M3.5 10h17" {...common} /></svg>;
    case "arrow-left":
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 6.5 9 12l5.5 5.5M19 12H9.2" {...common} /></svg>;
    default:
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" {...common} /></svg>;
  }
}

export default function GroupSessionDetailPage({ sessionId, isMyView = false, onBack, onEdit, onCloseSession }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useAuth();

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [bookingState, setBookingState] = useState({
    alreadyJoined: false,
    joinedBookingId: null,
    status: null,
    telemostUrl: null,
    paymentUrl: null,
    isPaid: false,
  });

  const loadDetail = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const raw = isMyView
        ? await sessionsApi.getMyGroupSession(sessionId)
        : await sessionsApi.getPublicGroupSession(sessionId);

      if (!isMyView && (isConferenceShadowSession(raw) || !isJoinAllowed(raw))) {
        toast.error("Эта сессия недоступна для публичной записи");
        setDetail(null);
        return;
      }

      setDetail(raw || null);

      if (isMyView) {
        setBookingState({
          alreadyJoined: false,
          joinedBookingId: null,
          status: raw?.status || null,
          telemostUrl: pickTelemostUrl(raw),
          paymentUrl: pickPaymentUrl(raw),
          isPaid: false,
        });
      } else {
        const allAppointments = await appointmentsApi.listMyAll().catch(() => []);
        const matched = safeArray(allAppointments).find(
          (a) => String(pickGroupSessionIdFromAppointment(a) ?? "") === String(sessionId)
        );
        if (matched) {
          const status = String(matched?.status || matched?.bookingStatus || "").toUpperCase();
          const bookingId = matched?.id ?? matched?.bookingId ?? matched?.booking_id ?? null;
          const isCancelled = status === "CANCELLED" || status === "CANCELED";
          const isPaid = isPaidAppointmentLike(matched);
          setBookingState({
            alreadyJoined: !isCancelled || isPaid,
            joinedBookingId: bookingId != null ? String(bookingId) : null,
            status: status || raw?.status || null,
            telemostUrl: pickTelemostUrl(matched) || pickTelemostUrl(raw),
            paymentUrl: pickPaymentUrl(matched) || pickPaymentUrl(raw),
            isPaid,
          });
        } else {
          setBookingState({
            alreadyJoined: false,
            joinedBookingId: null,
            status: raw?.status || null,
            telemostUrl: pickTelemostUrl(raw),
            paymentUrl: pickPaymentUrl(raw),
            isPaid: false,
          });
        }
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Не удалось загрузить групповую сессию");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [isMyView, sessionId, toast]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const host = useMemo(() => pickPsychologist(detail, me), [detail, me]);
  const start = pickStart(detail);
  const end = pickEnd(detail);
  const participantsCount = Number(pickParticipants(detail) || 0);
  const capacity = Number(pickCapacity(detail) || 0) || null;
  const freePlaces = capacity ? Math.max(capacity - participantsCount, 0) : null;
  const price = pickPrice(detail);
  const duration = diffMinutes(start, end) || 90;
  const themeTitles = joinTitles(pickThemes(detail));
  const methodTitles = joinTitles(pickMethods(detail));
  const title = detail?.title || "Групповая сессия";
  const subtitle = detail?.subtitle || (isMyView
    ? "Подробная карточка вашей групповой встречи с управлением, участниками и условиями записи."
    : "Групповая сессия с подробным описанием, ведущим и привычным механизмом записи через оплату.");
  const hostName = host?.name || host?.fullName || host?.displayName || host?.login || host?.email || "Ведущий сессии";
  const hostRole = pickHostRole(host);
  const hostBio = pickHostBio(detail, host);
  const hostAvatar = host?.avatarUrl || host?.avatar_url || host?.photoUrl || null;
  const experienceYears = pickExperienceYears(detail, host);
  const cover = pickCover(detail);
  const participants = safeArray(detail?.participants).map((item) => item?.user || item).filter(Boolean);
  const canClose = participantsCount === 0 && !["CANCELLED", "CANCELED", "CLOSED"].includes(toCode(detail?.status));
  const isPubliclyJoinable = isJoinAllowed(detail) && !isConferenceShadowSession(detail);

  const primaryAction = useMemo(() => {
    if (isMyView) return null;
    const paid = bookingState.isPaid || isPaidStatus(bookingState.status);
    if (paid) {
      return {
        label: bookingState.telemostUrl ? "Открыть встречу" : "Доступ откроется позже",
        disabled: !bookingState.telemostUrl,
      };
    }
    if (bookingState.alreadyJoined) {
      if (Number(price || 0) > 0) {
        return {
          label: bookingState.paymentUrl ? "Перейти к оплате" : "Оплатить участие",
          disabled: false,
        };
      }
      return { label: "Вы уже записаны", disabled: true };
    }
    return {
      label: Number(price || 0) > 0 ? "Записаться и оплатить" : "Записаться",
      disabled: ["CANCELLED", "CANCELED", "CLOSED"].includes(toCode(detail?.status)) || !isPubliclyJoinable,
    };
  }, [bookingState.alreadyJoined, bookingState.isPaid, bookingState.paymentUrl, bookingState.status, bookingState.telemostUrl, detail?.status, isMyView, isPubliclyJoinable, price]);

  const handlePrimaryAction = useCallback(async () => {
    if (!detail || isMyView || !primaryAction || primaryAction.disabled || actionLoading) return;

    if (bookingState.isPaid || isPaidStatus(bookingState.status)) {
      if (bookingState.telemostUrl) {
        assignExternalUrl(bookingState.telemostUrl);
      } else {
        toast.info("Ссылка на встречу появится ближе к началу сессии");
      }
      return;
    }

    try {
      setActionLoading(true);
      let joinRes = null;
      if (!bookingState.alreadyJoined) {
        joinRes = await sessionsApi.joinGroupSession(sessionId);
      }

      const joinedBookingId =
        joinRes?.bookingId ||
        joinRes?.id ||
        joinRes?.booking?.id ||
        bookingState.joinedBookingId ||
        null;

      appointmentsApi.rememberLocalGroupJoin(sessionId, joinedBookingId, {
        title,
        coverUrl: cover,
        status: Number(price || 0) > 0 ? "PENDING_PAYMENT" : "BOOKED",
        expiresAt: Date.now() + 15 * 60 * 1000,
      });

      if (Number(price || 0) > 0) {
        const pay = await sessionsApi.startGroupPayment(sessionId, {});
        const paymentUrl = readPaymentUrl(pay) || bookingState.paymentUrl;
        if (paymentUrl) {
          toast.success(bookingState.alreadyJoined ? "Открываем оплату…" : "Запись создана, переходим к оплате…");
          assignExternalUrl(paymentUrl);
          setBookingState((prev) => ({
            ...prev,
            alreadyJoined: true,
            joinedBookingId: joinedBookingId != null ? String(joinedBookingId) : prev.joinedBookingId,
            paymentUrl,
            status: prev.status || "PENDING_PAYMENT",
          }));
          return;
        }
        toast.info("Запись создана. Ссылка на оплату временно недоступна.");
      } else {
        toast.success("Вы записались на сессию");
      }

      await loadDetail();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Не удалось оформить запись");
    } finally {
      setActionLoading(false);
    }
  }, [actionLoading, bookingState.alreadyJoined, bookingState.isPaid, bookingState.joinedBookingId, bookingState.paymentUrl, bookingState.status, bookingState.telemostUrl, cover, detail, isMyView, loadDetail, price, primaryAction, sessionId, title, toast]);

  const participationSteps = [
    {
      title: "Выберите участие",
      text: "Ознакомьтесь с деталями сессии и выберите подходящее участие.",
    },
    {
      title: "Подтвердите запись",
      text: "Нажмите кнопку записи, чтобы забронировать место в группе.",
    },
    {
      title: "Оплатите участие",
      text: "Используется привычный механизм записи: после оплаты бронь подтверждается автоматически.",
    },
    {
      title: "Получите доступ",
      text: "После успешной оплаты вы получите доступ к сессии и ссылку на встречу.",
    },
  ];

  const expectationCards = [
    {
      icon: "shield",
      title: "Безопасное пространство",
      text: "Конфиденциальность, уважительное общение и бережный темп работы в группе.",
      tone: "mint",
    },
    {
      icon: "spark",
      title: methodTitles[0] || "Практики поддержки",
      text: methodTitles.length
        ? `Методы сессии: ${methodTitles.join(", ")}.`
        : "Практики самоподдержки, дыхания и мягкой стабилизации состояния.",
      tone: "purple",
    },
    {
      icon: "chat",
      title: "Обратная связь специалиста",
      text: "Ведущий помогает удерживать фокус встречи и дает понятные ориентиры участникам.",
      tone: "peach",
    },
    {
      icon: "users",
      title: themeTitles[0] || "Групповая динамика",
      text: themeTitles.length
        ? `Темы встречи: ${themeTitles.slice(0, 2).join(", ")}${themeTitles.length > 2 ? "…" : ""}.`
        : "Поддержка группы помогает почувствовать, что вы не одни в своём опыте.",
      tone: "blue",
    },
  ];

  const rules = [
    "Конфиденциальность всего, что происходит в группе.",
    "Уважительное отношение к себе и другим участникам.",
    "Говорим от своего имени и по желанию делимся опытом.",
    "Активное участие и открытость процессу.",
  ];

  const paymentMethods = ["Карта", "СБП", "Другие"];
  const sidebarCountLabel = capacity ? `${participantsCount} из ${capacity} участников` : `${participantsCount} участников`;
  const questionButtonLabel = isMyView ? "Открыть чат" : "Задать вопрос";

  if (loading) {
    return (
      <div className="b-gs-page b-gs-page--loading">
        <div className="b-gs-page__skeleton" />
        <div className="b-gs-page__skeleton b-gs-page__skeleton--hero" />
        <div className="b-gs-page__skeleton b-gs-page__skeleton--grid" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="b-gs-page b-gs-page--empty">
        <button type="button" className="b-gs-page__back" onClick={onBack}>
          <span className="b-gs-page__backIcon"><GsMiniIcon name="arrow-left" /></span>
          Назад к групповым сессиям
        </button>
        <div className="b-group-sessions__empty">
          <div className="b-group-sessions__empty-title">Сессия не найдена</div>
          <p>Не удалось загрузить детали этой групповой сессии.</p>
          <button type="button" className="b-btn" onClick={onBack}>Вернуться</button>
        </div>
      </div>
    );
  }

  return (
    <div className="b-gs-page">
      <button type="button" className="b-gs-page__back" onClick={onBack}>
        <span className="b-gs-page__backIcon"><GsMiniIcon name="arrow-left" /></span>
        Назад к групповым сессиям
      </button>

      <div className="b-gs-page__head">
        <div className="b-gs-page__headMain">
          <div className={`b-gs-status b-gs-status--${statusKind(detail?.status || "OPEN")}`}>
            {humanStatus(detail?.status || "OPEN")}
          </div>
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
      </div>

      <div className="b-gs-page__layout">
        <main className="b-gs-page__main">
          <section className="b-gs-page__heroCard">
            <div className="b-gs-page__coverWrap">
              {cover ? (
                <img className="b-gs-page__cover" src={resolveUrl(cover)} alt={title} loading="lazy" decoding="async" />
              ) : (
                <div className={`b-gs-page__coverPh b-gs-page__coverPh--${PLACEHOLDER_GRADIENTS[(Number(sessionId) || 1) % PLACEHOLDER_GRADIENTS.length]}`} />
              )}
            </div>
            <div className="b-gs-page__quickFacts">
              <div className="b-gs-page__fact">
                <span className="b-gs-page__factIcon"><GsMiniIcon name="calendar" /></span>
                <div>
                  <strong>{`${formatDateShort(start)}, ${formatTime(start)}`}</strong>
                  <small>Дата и время</small>
                </div>
              </div>
              <div className="b-gs-page__fact">
                <span className="b-gs-page__factIcon"><GsMiniIcon name="wifi" /></span>
                <div>
                  <strong>{pickFormat(detail)}</strong>
                  <small>Формат</small>
                </div>
              </div>
              <div className="b-gs-page__fact">
                <span className="b-gs-page__factIcon"><GsMiniIcon name="users" /></span>
                <div>
                  <strong>{capacity ? `${participantsCount}/${capacity}` : participantsCount}</strong>
                  <small>{freePlaces != null ? `Свободных мест: ${freePlaces}` : "Количество участников"}</small>
                </div>
              </div>
              <div className="b-gs-page__fact">
                <span className="b-gs-page__factIcon"><GsMiniIcon name="user" /></span>
                <div>
                  <strong>{hostName}</strong>
                  <small>Ведущий</small>
                </div>
              </div>
              <div className="b-gs-page__fact">
                <span className="b-gs-page__factIcon"><GsMiniIcon name="clock" /></span>
                <div>
                  <strong>{duration} минут</strong>
                  <small>Длительность</small>
                </div>
              </div>
            </div>
          </section>

          <section className="b-gs-page__panel">
            <div className="b-gs-page__sectionTitle">О сессии</div>
            <div className="b-gs-page__bodyText">
              {(detail?.description || "Описание пока не добавлено.")}
            </div>

            <div className="b-gs-page__sectionTitle b-gs-page__sectionTitle--spaced">Что вас ждёт</div>
            <div className="b-gs-page__expectations">
              {expectationCards.map((card) => (
                <article key={card.title} className={`b-gs-page__expectCard b-gs-page__expectCard--${card.tone}`}>
                  <span className="b-gs-page__expectIcon"><GsMiniIcon name={card.icon} /></span>
                  <div className="b-gs-page__expectTitle">{card.title}</div>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </section>

          <div className="b-gs-page__dual">
            <section className="b-gs-page__panel b-gs-page__panel--host">
              <div className="b-gs-page__sectionTitle">Ведущий</div>
              <div className="b-gs-page__hostCard">
                <div className="b-gs-page__hostAvatar">
                  {hostAvatar ? (
                    <img src={resolveUrl(hostAvatar)} alt={hostName} loading="lazy" decoding="async" />
                  ) : (
                    <div className="b-gs-page__hostAvatarPh">{initials(hostName)}</div>
                  )}
                </div>
                <div className="b-gs-page__hostMeta">
                  <div className="b-gs-page__hostName">{hostName}</div>
                  <div className="b-gs-page__hostRole">{hostRole}</div>
                  <p>{hostBio}</p>
                </div>
                {experienceYears ? (
                  <div className="b-gs-page__hostBadge">{experienceYears}+ лет<br />опыта работы</div>
                ) : null}
              </div>
            </section>

            <section className="b-gs-page__panel b-gs-page__panel--rules">
              <div className="b-gs-page__sectionTitle">Правила участия</div>
              <ul className="b-gs-page__rules">
                {rules.map((rule) => (
                  <li key={rule}>
                    <span className="b-gs-page__ruleIcon"><GsMiniIcon name="check" /></span>
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <section className="b-gs-page__panel">
            <div className="b-gs-page__sectionTitle">Механизм записи</div>
            <div className="b-gs-page__steps">
              {participationSteps.map((step, index) => (
                <div key={step.title} className="b-gs-page__step">
                  <div className="b-gs-page__stepNum">{index + 1}</div>
                  <div className="b-gs-page__stepTitle">{step.title}</div>
                  <p>{step.text}</p>
                </div>
              ))}
            </div>
            <div className="b-gs-page__stepNote">
              <span className="b-gs-page__stepNoteIcon"><GsMiniIcon name="lock" /></span>
              {isMyView
                ? "Оплата и запись у участников проходят автоматически по привычному механизму после нажатия на кнопку участия."
                : "Ваша запись подтверждается автоматически после успешной оплаты."}
            </div>
          </section>

          {isMyView ? (
            <section className="b-gs-page__panel">
              <div className="b-gs-page__sectionTitle">Участники</div>
              {participants.length ? (
                <div className="b-gs-page__participantsList">
                  {participants.map((user, index) => {
                    const avatar = user?.avatarUrl || user?.avatar_url || null;
                    return (
                      <div className="b-gs-page__participant" key={user?.id || user?.email || `${participantLabel(user)}_${index}`}>
                        <div className="b-gs-page__participantAvatar">
                          {avatar ? <img src={resolveUrl(avatar)} alt={participantLabel(user)} loading="lazy" decoding="async" /> : <div className="b-gs-page__participantAvatarPh">{initials(participantLabel(user))}</div>}
                        </div>
                        <div className="b-gs-page__participantMeta">
                          <strong>{participantLabel(user)}</strong>
                          <small>{user?.email || user?.role || "Участник"}</small>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="b-gs-page__emptyNote">Пока никто не записался на эту сессию.</div>
              )}
            </section>
          ) : null}
        </main>

        <aside className="b-gs-page__aside">
          <section className="b-gs-page__sideCard">
            <div className="b-gs-page__sideLabel">{isMyView ? "Управление сессией" : "Участие в сессии"}</div>
            <div className="b-gs-page__price">{formatPrice(price)}</div>
            <div className="b-gs-page__priceSub">{isMyView ? "текущая стоимость участия" : "за одно участие"}</div>

            <div className="b-gs-page__sideMeta">
              <div className="b-gs-page__sideMetaRow">
                <span className="b-gs-page__sideMetaIcon"><GsMiniIcon name="users" /></span>
                <div>
                  <strong>{freePlaces != null ? `Свободных мест: ${freePlaces} из ${capacity}` : `${participantsCount} участников`}</strong>
                </div>
              </div>
              <div className="b-gs-page__sideMetaRow">
                <span className="b-gs-page__sideMetaIcon"><GsMiniIcon name="calendar" /></span>
                <div>
                  <strong>Следующая сессия</strong>
                  <small>{fmtDateTimeRange(start, end)}</small>
                </div>
              </div>
              <div className="b-gs-page__sideMetaRow">
                <span className="b-gs-page__sideMetaIcon"><GsMiniIcon name="wifi" /></span>
                <div>
                  <strong>Формат</strong>
                  <small>{pickFormat(detail)} • {duration} минут</small>
                </div>
              </div>
            </div>

            <div className="b-gs-page__tariff">
              <div className="b-gs-page__tariffRow">
                <span>{isMyView ? "Базовый тариф" : "Участие в сессии"}</span>
                <b>{formatPrice(price)}</b>
              </div>
            </div>

            {isMyView ? (
              <div className="b-gs-page__manageActions">
                <button type="button" className="b-btn" onClick={() => onEdit?.(sessionId)}>Редактировать</button>
                <button type="button" className="b-btn b-btn--transparent" onClick={() => onCloseSession?.(detail)} disabled={!canClose}>Закрыть</button>
              </div>
            ) : (
              <button type="button" className="b-gs-page__primaryBtn" onClick={handlePrimaryAction} disabled={actionLoading || primaryAction?.disabled}>
                {actionLoading ? "Обработка…" : primaryAction?.label}
              </button>
            )}

            <div className="b-gs-page__sideNote">
              <span className="b-gs-page__sideNoteIcon"><GsMiniIcon name="lock" /></span>
              <span>
                {isMyView
                  ? "Участники используют привычный механизм записи: после оплаты они автоматически записываются на сессию и получают доступ."
                  : "Используем привычный механизм записи: после оплаты вы автоматически записываетесь на сессию и получаете доступ."}
              </span>
            </div>
          </section>

          <section className="b-gs-page__sideCard">
            <div className="b-gs-page__sectionTitle b-gs-page__sectionTitle--small">Оплата</div>
            <div className="b-gs-page__paymentMethods">
              {paymentMethods.map((method, index) => (
                <div key={method} className={`b-gs-page__paymentMethod ${index === 0 ? "is-active" : ""}`}>
                  <span className="b-gs-page__paymentMethodIcon"><GsMiniIcon name={index === 0 ? "card" : index === 1 ? "spark" : "chat"} /></span>
                  {method}
                </div>
              ))}
            </div>
            <div className="b-gs-page__securePay">
              <span className="b-gs-page__securePayIcon"><GsMiniIcon name="shield" /></span>
              <div>
                <strong>Безопасная оплата</strong>
                <small>Ваши данные надёжно защищены.</small>
              </div>
            </div>
            <div className="b-gs-page__receipt">
              <div><span>Участие</span><b>{formatPrice(price)}</b></div>
              <div><span>Сервисный сбор</span><b>0 ₽</b></div>
            </div>
            <div className="b-gs-page__receiptTotal"><span>Итого</span><b>{formatPrice(price)}</b></div>
            <button type="button" className="b-gs-page__secondaryBtn" onClick={() => navigate("/chat")}>{questionButtonLabel}</button>
          </section>

          <section className="b-gs-page__sideCard">
            <div className="b-gs-page__sectionTitle b-gs-page__sectionTitle--small">Уже записались</div>
            {participants.length ? (
              <>
                <div className="b-gs-page__miniAvatars">
                  {participants.slice(0, 6).map((user, index) => {
                    const avatar = user?.avatarUrl || user?.avatar_url || null;
                    return (
                      <div key={user?.id || user?.email || `${participantLabel(user)}_${index}`} className="b-gs-page__miniAvatar">
                        {avatar ? <img src={resolveUrl(avatar)} alt={participantLabel(user)} loading="lazy" decoding="async" /> : <div className="b-gs-page__miniAvatarPh">{initials(participantLabel(user))}</div>}
                      </div>
                    );
                  })}
                  {capacity && participantsCount > 6 ? <div className="b-gs-page__miniAvatar b-gs-page__miniAvatar--more">+{participantsCount - 6}</div> : null}
                </div>
                <div className="b-gs-page__miniNote">{sidebarCountLabel}</div>
              </>
            ) : (
              <div className="b-gs-page__miniNote">{participantsCount > 0 ? sidebarCountLabel : "Пока никто не записался."}</div>
            )}
          </section>

          <section className="b-gs-page__sideCard">
            <div className="b-gs-page__sectionTitle b-gs-page__sectionTitle--small">Политика отмены</div>
            <div className="b-gs-page__cancelText">
              Отмена возможна не позднее чем за 24 часа до начала сессии с возвратом 100% оплаты. При отмене менее чем за 24 часа возврат не осуществляется.
            </div>
            <button type="button" className="b-gs-page__linkBtn" onClick={() => toast.info("Для отмены участия используйте раздел записей или обратитесь в поддержку.")}>Подробнее</button>
          </section>
        </aside>
      </div>
    </div>
  );
}
